import axios from 'axios';

/**
 * API Client - HttpOnly Cookie tabanlı auth
 * Token'lar JS tarafından görünmez, backend HttpOnly cookie set eder.
 */

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3333/api';

// Race condition koruması - tek bir refresh promise
let _refreshPromise = null;

async function doRefresh() {
  if (_refreshPromise) return _refreshPromise;

  _refreshPromise = axios.post(`${API_URL}/auth/refresh`, {}, {
    withCredentials: true,
    _isRefresh: true, // interceptor'ın bu isteği tekrar yakalamaması için
  }).finally(() => { _refreshPromise = null; });

  return _refreshPromise;
}

// Axios instance - withCredentials ile cookie otomatik gönderilir
export const apiClient = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 10000,
  withCredentials: true, // Cookie'leri otomatik gönder/al
});

apiClient.interceptors.response.use(
  (response) => response.data,
  async (error) => {
    const originalRequest = error.config;

    // Refresh isteğinin kendisi 401 aldıysa — sonsuz döngüyü önle, sessizce çık
    if (originalRequest._isRefresh) {
      window.dispatchEvent(new CustomEvent('auth:logout'));
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        await doRefresh();
        return apiClient(originalRequest);
      } catch {
        window.dispatchEvent(new CustomEvent('auth:logout'));
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  }
);

// ============================================
// PUBLIC ENDPOINTS
// ============================================

export const api = {
  getProducts: (params = {}) => apiClient.get('/products', { params }),
  getProduct: (slug) => apiClient.get(`/products/${slug}`),
  getProductPrice: (id) => apiClient.get(`/products/${id}/price`),
  getCategories: (menu) => apiClient.get('/categories', { params: menu ? { menu } : {} }),
  getPrices: () => apiClient.get('/prices'),
  getBlogPosts: (params = {}) => apiClient.get('/blog', { params }),
  getBlogPost: (slug) => apiClient.get(`/blog/${slug}`),
  createGuestOrder: (orderData) => apiClient.post('/orders/guest', orderData, { timeout: 60000 }),
  trackOrder: (orderNumber, phone, email) => apiClient.get(`/orders/track/${orderNumber}`, { params: { phone, email } }),

  // AUTH
  register: (userData) => apiClient.post('/auth/register', userData),
  login: (credentials) => apiClient.post('/auth/login', credentials),
  logout: () => apiClient.post('/auth/logout'),
  refreshToken: () => apiClient.post('/auth/refresh'),
  forgotPassword: (data) => apiClient.post('/auth/forgot-password', data),
  resetPassword: (token, newPassword) => apiClient.post('/auth/reset-password', { token, newPassword }),
  verifyEmail: (token) => apiClient.get(`/auth/verify-email/${token}`),
  checkAvailability: (field, value) => apiClient.get('/auth/check-availability', { params: { field, value } }),
  resendVerification: () => apiClient.post('/auth/resend-verification'),

  // PROTECTED
  getProfile: () => apiClient.get('/user/profile'),
  updateProfile: (userData) => apiClient.put('/user/profile', userData),
  addRecentlyViewed: (productId) => apiClient.post(`/user/recently-viewed/${productId}`),
  changePassword: (passwordData) => apiClient.post('/user/change-password', passwordData),
  getAddresses: () => apiClient.get('/user/addresses'),
  createAddress: (addressData) => apiClient.post('/user/addresses', addressData),
  updateAddress: (id, addressData) => apiClient.put(`/user/addresses/${id}`, addressData),
  deleteAddress: (id) => apiClient.delete(`/user/addresses/${id}`),
  getOrders: () => apiClient.get('/orders'),
  getOrder: (id) => apiClient.get(`/orders/${id}`),
  createOrder: (orderData) => apiClient.post('/orders', orderData),
  getFavorites: () => apiClient.get('/favorites'),
  addFavorite: (productId) => apiClient.post('/favorites', { productId }),
  removeFavorite: (id) => apiClient.delete(`/favorites/${id}`),

  // ADMIN
  admin: {
    getOrders: (params = {}) => apiClient.get('/admin/orders', { params }),
    getOrder: (id) => apiClient.get(`/admin/orders/${id}`),
    updateOrder: (id, orderData) => apiClient.put(`/admin/orders/${id}`, orderData),
    uploadInvoice: (id, file) => {
      const formData = new FormData();
      formData.append('invoice', file);
      return apiClient.post(`/admin/orders/${id}/invoice`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
    },
    getUsers: (params = {}) => apiClient.get('/admin/users', { params }),
    getDashboardStats: () => apiClient.get('/admin/dashboard/stats'),
    getSettings: () => apiClient.get('/admin/settings'),
    updateSettings: (settings) => apiClient.put('/admin/settings', settings),
  },
};

export default api;
